import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, FileDown, FileType, Pencil, Send, CheckCircle2, XCircle, History, Eye, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, downloadFile, errMsg, money, CURRENCIES } from "../api";
import { useAuth } from "../auth";
import { myCompaniesOf } from "../components/CompanyScopeBar";

const STATUS_TONE = { "Taslak": "status-amber", "Gönderildi": "status-blue", "Onaylandı": "status-green", "İptal Edildi": "status-red" };
const emptyLine = () => ({ project_name: "", category: "", description: "", amount: "", currency: "TRY" });
const DEFAULT_INTRO = "... İli, ... İlçesi sınırları içerisinde planlanan ... Projesi için 1/5000 Ölçekli Nazım İmar Planı ve 1/1000 Ölçekli Uygulama İmar Planı dosyasının hazırlanarak onaya sunulması iş ve işlemlerine ilişkin teklifimiz aşağıdaki gibidir;";
const emptyQuote = (company = "REEN MT Project") => ({ company, employer_short: "", subject: "", date: new Date().toISOString().slice(0, 10), intro_text: DEFAULT_INTRO, lines: [emptyLine()], notes: [], validity_days: 15, vat_rate: 20, status: "Taslak" });

export default function Quotes() {
  const { can, user } = useAuth();
  const myCompanies = myCompaniesOf(user);
  const editable = can("admin", "project_manager");
  const [items, setItems] = useState([]);
  const [employers, setEmployers] = useState([]);
  const [cats, setCats] = useState([]);
  const [noteLib, setNoteLib] = useState([]);
  const [fStatus, setFStatus] = useState("");
  const [fCompany, setFCompany] = useState("");
  const [fQuery, setFQuery] = useState("");
  const [sortBy, setSortBy] = useState("date_desc");
  const [editor, setEditor] = useState(null);     // quote being edited/created
  const [preview, setPreview] = useState(null);    // quote being previewed
  const [approveFor, setApproveFor] = useState(null);

  const load = () => api.get("/quotes").then(r => setItems(r.data)).catch(() => toast.error("Teklifler yüklenemedi"));
  useEffect(() => {
    load();
    api.get("/employers").then(r => setEmployers(r.data)).catch(() => {});
    api.get("/work-items/categories").then(r => setCats(r.data.all || [])).catch(() => {});
    api.get("/quote-notes").then(r => setNoteLib(r.data)).catch(() => {});
  }, []);

  const quoteTotal = (q) => (q.lines || []).reduce((s, l) => s + (Number(l.amount) || 0), 0);

  const filtered = useMemo(() => {
    const term = fQuery.trim().toLocaleLowerCase("tr");
    let list = items.filter(q =>
      (!fStatus || q.status === fStatus) &&
      (!fCompany || q.company === fCompany) &&
      (!term || [q.quote_no, q.subject, q.employer_short].some(v => (v || "").toLocaleLowerCase("tr").includes(term)))
    );
    const cmp = {
      date_desc: (a, b) => (b.date || "").localeCompare(a.date || ""),
      date_asc: (a, b) => (a.date || "").localeCompare(b.date || ""),
      no_desc: (a, b) => (b.quote_no || "").localeCompare(a.quote_no || ""),
      no_asc: (a, b) => (a.quote_no || "").localeCompare(b.quote_no || ""),
      amount_desc: (a, b) => quoteTotal(b) - quoteTotal(a),
      amount_asc: (a, b) => quoteTotal(a) - quoteTotal(b),
      subject_asc: (a, b) => (a.subject || "").localeCompare(b.subject || "", "tr"),
      status_asc: (a, b) => (a.status || "").localeCompare(b.status || "", "tr"),
    }[sortBy];
    return [...list].sort(cmp);
  }, [items, fStatus, fCompany, fQuery, sortBy]);

  const saveQuote = async (q) => {
    const payload = {
      ...q, validity_days: Number(q.validity_days) || 15, vat_rate: Number(q.vat_rate) || 20,
      lines: (q.lines || []).filter(l => l.category).map(l => ({
        project_name: l.project_name, category: l.category, description: l.description,
        amount: Number(l.amount) || 0, currency: l.currency || "TRY",
      })),
    };
    if (!payload.subject.trim()) return toast.error("Konu girilmeli");
    if (!payload.lines.length) return toast.error("En az bir teklif kalemi (kategori) girilmeli");
    try {
      if (q.id) await api.put(`/quotes/${q.id}`, payload); else await api.post("/quotes", payload);
      toast.success(q.id ? "Teklif güncellendi" : "Teklif oluşturuldu");
      setEditor(null); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const setStatus = async (q, status) => { try { await api.post(`/quotes/${q.id}/status`, { status }); toast.success(`Durum: ${status}`); load(); } catch (err) { toast.error(errMsg(err)); } };
  const revise = async (q) => { if (!window.confirm("Yeni revizyon oluşturulsun mu? (R" + ((q.revision || 0) + 1) + ")")) return; try { const r = await api.post(`/quotes/${q.id}/revise`); toast.success(`Revizyon R${r.data.revision} oluşturuldu`); load(); setEditor(r.data); } catch (err) { toast.error(errMsg(err)); } };
  const remove = async (q) => { if (!window.confirm("Teklif çöp kutusuna taşınsın mı?")) return; try { await api.delete(`/quotes/${q.id}`); toast.success("Teklif silindi"); load(); } catch (err) { toast.error(errMsg(err)); } };
  const pdf = async (q) => { try { await downloadFile(`/quotes/${q.id}/pdf`, `${q.quote_no}_R${q.revision}.pdf`); toast.success("PDF indirildi"); } catch { toast.error("PDF oluşturulamadı"); } };
  const docx = async (q) => { try { await downloadFile(`/quotes/${q.id}/docx`, `${q.quote_no}_R${q.revision}.docx`); toast.success("Word indirildi"); } catch { toast.error("Word oluşturulamadı"); } };

  return (
    <main className="content" data-testid="page-quotes">
      <div className="page-head">
        <div>
          <span className="section-kicker">TEKLİF YÖNETİMİ</span>
          <h1>Teklifler ({filtered.length})</h1>
          <p>Şablon üzerinden teklif hazırlayın, PDF oluşturun, revize edin ve onaylanınca projeye dönüştürün.</p>
        </div>
        {editable && <button className="new-btn" onClick={() => setEditor(emptyQuote(myCompanies[0]))} data-testid="add-quote-button"><Plus size={17} /> Yeni Teklif</button>}
      </div>

      <div className="filter-bar">
        <input className="filter-select" style={{ minWidth: 220 }} placeholder="Ara: teklif no, konu, alıcı…" value={fQuery} onChange={(e) => setFQuery(e.target.value)} data-testid="quotes-filter-search" />
        {myCompanies.length > 1 && (
          <select className="filter-select" value={fCompany} onChange={(e) => setFCompany(e.target.value)} data-testid="quotes-filter-company">
            <option value="">Tüm şirketler</option>
            {myCompanies.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        <select className="filter-select" value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="quotes-filter-status">
          <option value="">Tüm durumlar</option>
          {["Taslak", "Gönderildi", "Onaylandı", "İptal Edildi"].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className="filter-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)} data-testid="quotes-sort">
          <option value="date_desc">Tarih (yeni → eski)</option>
          <option value="date_asc">Tarih (eski → yeni)</option>
          <option value="no_desc">Teklif No (Z → A)</option>
          <option value="no_asc">Teklif No (A → Z)</option>
          <option value="amount_desc">Tutar (yüksek → düşük)</option>
          <option value="amount_asc">Tutar (düşük → yüksek)</option>
          <option value="subject_asc">Konu (A → Z)</option>
          <option value="status_asc">Duruma göre</option>
        </select>
      </div>

      <section className="panel">
        <div className="project-table" data-testid="quotes-table">
          <div className="table-row table-head quote-row"><span>TEKLİF NO</span><span>KONU</span><span>ŞİRKET / ALICI</span><span>TUTAR</span><span>DURUM</span><span></span></div>
          {filtered.map((q, i) => (
            <div className="table-row quote-row" key={q.id} data-testid={`quote-row-${i}`}>
              <div><b>{q.quote_no}</b><small>R{q.revision} · {(q.date || "").slice(0, 10)}</small></div>
              <div><b className="text-line">{q.subject}</b><small className="text-line">{(q.lines || []).length} kalem</small></div>
              <div><b className="text-line">{q.company}</b><small className="text-line">{q.employer_short || "— alıcı —"}</small></div>
              <b className="mono-small">{money(quoteTotal(q), (q.lines || [{}])[0]?.currency || "TRY")}</b>
              <span><em className={`status ${STATUS_TONE[q.status] || "status-amber"}`}>{q.status}</em></span>
              <div className="row-actions">
                <button className="row-action" title="Önizle" onClick={() => setPreview(q)} data-testid={`quote-preview-${i}`}><Eye size={13} /></button>
                <button className="row-action" title="PDF indir" onClick={() => pdf(q)} data-testid={`quote-pdf-${i}`}><FileDown size={13} /></button>
                <button className="row-action" title="Word indir" onClick={() => docx(q)} data-testid={`quote-docx-${i}`}><FileType size={13} /></button>
                {editable && q.status !== "Onaylandı" && <button className="row-action" title="Düzenle" onClick={() => setEditor({ ...q, lines: q.lines?.length ? q.lines : [emptyLine()] })} data-testid={`quote-edit-${i}`}><Pencil size={13} /></button>}
                {editable && q.status === "Taslak" && <button className="row-action" title="Gönderildi işaretle" onClick={() => setStatus(q, "Gönderildi")} data-testid={`quote-send-${i}`}><Send size={13} /></button>}
                {editable && q.status !== "Onaylandı" && q.status !== "İptal Edildi" && <button className="row-action success" title="Onayla → Projeye aktar" onClick={() => setApproveFor(q)} data-testid={`quote-approve-${i}`}><CheckCircle2 size={13} /></button>}
                {editable && q.status !== "İptal Edildi" && q.status !== "Onaylandı" && <button className="row-action" title="İptal et" onClick={() => setStatus(q, "İptal Edildi")} data-testid={`quote-cancel-${i}`}><XCircle size={13} /></button>}
                {editable && <button className="row-action" title="Revize et" onClick={() => revise(q)} data-testid={`quote-revise-${i}`}><History size={13} /></button>}
                {editable && <button className="row-action danger" title="Sil" onClick={() => remove(q)} data-testid={`quote-delete-${i}`}><Trash2 size={13} /></button>}
              </div>
            </div>
          ))}
          {!filtered.length && <div className="empty-hint">Henüz teklif yok. "Yeni Teklif" ile başlayın.</div>}
        </div>
      </section>

      {editor && <QuoteEditor q={editor} setQ={setEditor} cats={cats} employers={employers} noteLib={noteLib} setNoteLib={setNoteLib} myCompanies={myCompanies} onSave={saveQuote} onClose={() => setEditor(null)} onPreview={setPreview} />}
      {preview && <QuotePreview q={preview} total={quoteTotal(preview)} onClose={() => setPreview(null)} onPdf={() => pdf(preview)} onDocx={() => docx(preview)} />}
      {approveFor && <ApproveModal q={approveFor} employers={employers} myCompanies={myCompanies} onClose={() => setApproveFor(null)} onDone={() => { setApproveFor(null); load(); }} />}
    </main>
  );
}

function QuoteEditor({ q, setQ, cats, employers, noteLib, setNoteLib, myCompanies, onSave, onClose, onPreview }) {
  const [newNote, setNewNote] = useState("");
  const set = (k) => (e) => setQ({ ...q, [k]: e.target.value });
  const setLine = (i, k, v) => setQ({ ...q, lines: q.lines.map((l, j) => j === i ? { ...l, [k]: v } : l) });
  const addLine = () => setQ({ ...q, lines: [...q.lines, emptyLine()] });
  const rmLine = (i) => setQ({ ...q, lines: q.lines.filter((_, j) => j !== i) });
  const toggleNote = (txt) => setQ({ ...q, notes: q.notes.includes(txt) ? q.notes.filter(n => n !== txt) : [...q.notes, txt] });
  const addNoteToLib = async () => {
    const txt = newNote.trim(); if (!txt) return;
    try { const r = await api.post("/quote-notes", { text: txt }); setNoteLib((l) => l.find(x => x.id === r.data.id) ? l : [...l, r.data]); setQ({ ...q, notes: [...q.notes, txt] }); setNewNote(""); }
    catch (err) { toast.error(errMsg(err)); }
  };
  const total = q.lines.reduce((s, l) => s + (Number(l.amount) || 0), 0);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <form className="modal modal-xwide" onClick={(e) => e.stopPropagation()} onSubmit={(e) => { e.preventDefault(); onSave(q); }} data-testid="quote-editor-modal">
        <div className="modal-head">
          <div><span className="eyebrow">{q.id ? `DÜZENLE · ${q.quote_no} · R${q.revision}` : "YENİ TEKLİF"}</span><h2>{q.subject || "Teklif hazırla"}</h2></div>
          <button type="button" onClick={onClose} data-testid="quote-editor-close"><X size={18} /></button>
        </div>
        <div className="form-grid three">
          <label>Gönderen Şirket<select value={q.company} onChange={set("company")} data-testid="quote-company-select">{myCompanies.map(c => <option key={c}>{c}</option>)}</select></label>
          <label>Alıcı Firma (İşveren)<input list="qe-emps" value={q.employer_short || ""} onChange={set("employer_short")} placeholder="Opsiyonel" data-testid="quote-employer-input" /><datalist id="qe-emps">{employers.map(e => <option key={e.id} value={e.short_name}>{e.name}</option>)}</datalist></label>
          <label>Tarih<input type="date" value={q.date} onChange={set("date")} data-testid="quote-date-input" /></label>
        </div>
        <label>Konu<input required value={q.subject} onChange={set("subject")} placeholder="Örn. Manisa Kula-Gördes GES İzin Süreçleri Teklifi" data-testid="quote-subject-input" /></label>
        <label>Teklif Metni (giriş)<textarea rows={2} value={q.intro_text || ""} onChange={set("intro_text")} data-testid="quote-intro-input" /></label>

        <div className="quote-lines-head"><span className="eyebrow">TEKLİF KALEMLERİ</span><button type="button" className="view-all" onClick={addLine} data-testid="quote-add-line"><Plus size={13} /> Kalem ekle</button></div>
        <div className="quote-lines">
          <div className="quote-line quote-line-head"><span>PROJE</span><span>İŞ KALEMİ</span><span>AÇIKLAMA</span><span>TUTAR</span><span></span></div>
          {q.lines.map((l, i) => (
            <div className="quote-line" key={i} data-testid={`quote-line-${i}`}>
              <input value={l.project_name || ""} onChange={(e) => setLine(i, "project_name", e.target.value)} placeholder="Proje adı" data-testid={`quote-line-project-${i}`} />
              <input list="qe-cats" value={l.category || ""} onChange={(e) => setLine(i, "category", e.target.value)} placeholder="Kategori" data-testid={`quote-line-category-${i}`} />
              <input value={l.description || ""} onChange={(e) => setLine(i, "description", e.target.value)} placeholder="Açıklama" data-testid={`quote-line-desc-${i}`} />
              <input type="number" step="0.01" value={l.amount ?? ""} onChange={(e) => setLine(i, "amount", e.target.value)} placeholder="0" data-testid={`quote-line-amount-${i}`} />
              <button type="button" className="row-action danger" onClick={() => rmLine(i)} data-testid={`quote-line-remove-${i}`}><Trash2 size={12} /></button>
            </div>
          ))}
          <datalist id="qe-cats">{cats.map(c => <option key={c} value={c} />)}</datalist>
        </div>
        <div className="quote-total-row">
          <div className="form-grid">
            <label>Para Birimi<select value={q.lines[0]?.currency || "TRY"} onChange={(e) => setQ({ ...q, lines: q.lines.map(l => ({ ...l, currency: e.target.value })) })} data-testid="quote-currency-select">{CURRENCIES.map(c => <option key={c}>{c}</option>)}</select></label>
            <label>KDV Oranı %<input type="number" value={q.vat_rate} onChange={set("vat_rate")} data-testid="quote-vat-input" /></label>
            <label>Geçerlilik (gün)<input type="number" value={q.validity_days} onChange={set("validity_days")} data-testid="quote-validity-input" /></label>
          </div>
          <div className="quote-grand" data-testid="quote-grand-total">GENEL TOPLAM (KDV hariç)<b>{money(total, q.lines[0]?.currency || "TRY")}</b></div>
        </div>

        <div className="quote-lines-head"><span className="eyebrow">ŞARTLAR & NOTLAR</span></div>
        <div className="quote-notes-lib" data-testid="quote-notes-lib">
          {noteLib.map((n) => (
            <label key={n.id} className="check-label"><input type="checkbox" checked={q.notes.includes(n.text)} onChange={() => toggleNote(n.text)} /> {n.text}</label>
          ))}
          {!noteLib.length && <small className="muted-cell">Kayıtlı not yok — aşağıdan ekleyin.</small>}
        </div>
        <div className="quote-add-note">
          <input value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Yeni not ekle (listeye kaydedilir)" data-testid="quote-new-note-input" />
          <button type="button" className="ghost-btn" onClick={addNoteToLib} data-testid="quote-add-note-button"><Plus size={13} /> Ekle</button>
        </div>
        {q.notes.length > 0 && <div className="quote-chosen-notes">{q.notes.map((n, i) => <span key={i} className="note-chip">{n}</span>)}</div>}

        <div className="quote-editor-actions">
          <button type="button" className="ghost-btn" onClick={() => onPreview(q)} data-testid="quote-editor-preview"><Eye size={15} /> Önizle</button>
          <button className="new-btn" data-testid="quote-editor-save"><FileText size={15} /> {q.id ? "Kaydet" : "Teklif Oluştur"}</button>
        </div>
      </form>
    </div>
  );
}

function QuotePreview({ q, total, onClose, onPdf, onDocx }) {
  const cur = (q.lines || [{}])[0]?.currency || "TRY";
  const groups = {};
  (q.lines || []).forEach(l => { (groups[l.project_name || q.subject || "Teklif"] = groups[l.project_name || q.subject || "Teklif"] || []).push(l); });
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-xwide quote-preview" onClick={(e) => e.stopPropagation()} data-testid="quote-preview-modal">
        <div className="modal-head"><div><span className="eyebrow">{q.company} · {q.quote_no || "TASLAK"} · R{q.revision ?? 0}</span><h2>TEKLİF MEKTUBU</h2></div><button type="button" onClick={onClose} data-testid="quote-preview-close"><X size={18} /></button></div>
        <div className="quote-doc">
          <p className="qd-meta">Tarih: <b>{(q.date || "").slice(0, 10)}</b></p>
          {q.employer_short && <p style={{ textAlign: "center" }}><b>{q.employer_short}</b></p>}
          <p><b>Konu:</b> {q.subject}</p>
          {q.intro_text && <p className="qd-intro">{q.intro_text}</p>}
          {Object.entries(groups).map(([gname, glines]) => (
            <div key={gname} className="qd-group">
              {Object.keys(groups).length > 1 && <h4>{gname}</h4>}
              <table className="qd-table"><thead><tr><th>İş Kalemi</th><th>Açıklama</th><th>Tutar</th></tr></thead>
                <tbody>{glines.map((l, i) => <tr key={i}><td>{l.category}</td><td>{l.description || "—"}</td><td>{money(l.amount, cur)}</td></tr>)}
                  <tr className="qd-sub"><td colSpan={2}>TOPLAM</td><td>{money(glines.reduce((s, l) => s + (Number(l.amount) || 0), 0), cur)}</td></tr></tbody>
              </table>
            </div>
          ))}
          <p className="qd-grand"><b>GENEL TOPLAM (KDV hariç): {money(total, cur)}</b></p>
          <p className="qd-note">Verilen fiyatlara KDV (%{q.vat_rate}) dahil değildir.</p>
          {q.notes?.length > 0 && <div className="qd-notes"><h4>Şartlar ve Notlar</h4><ul>{q.notes.map((n, i) => <li key={i}>{n}</li>)}</ul></div>}
          <p className="qd-valid">Teklifimiz {q.validity_days} gün için geçerlidir.</p>
          <p className="qd-sign"><b>{q.company}</b></p>
        </div>
        {q.id && (
          <div className="quote-preview-actions">
            <button className="new-btn" onClick={onPdf} data-testid="quote-preview-pdf"><FileDown size={15} /> PDF indir</button>
            <button className="import-btn" onClick={onDocx} data-testid="quote-preview-docx"><FileType size={15} /> Word indir</button>
          </div>
        )}
      </div>
    </div>
  );
}

function ApproveModal({ q, employers, myCompanies, onClose, onDone }) {
  const [emp, setEmp] = useState(q.employer_short || "");
  const [company, setCompany] = useState(q.company || myCompanies[0]);
  const [busy, setBusy] = useState(false);
  const projectCount = new Set((q.lines || []).map(l => l.project_name || q.subject)).size;
  const approve = async () => {
    if (!emp.trim()) return toast.error("İşveren seçin");
    setBusy(true);
    try { const r = await api.post(`/quotes/${q.id}/approve`, { employer_short: emp.trim(), company }); toast.success(`Teklif onaylandı · ${r.data.count} proje oluşturuldu`); onDone(); }
    catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="quote-approve-modal">
        <div className="modal-head"><div><span className="eyebrow">TEKLİF ONAYI</span><h2>{q.quote_no} → Projeye aktar</h2></div><button type="button" onClick={onClose} data-testid="quote-approve-close"><X size={18} /></button></div>
        <p className="mini-info">Bu teklif onaylanınca <b>{projectCount} proje</b> oluşturulacak ve iş kalemleri işveren bedeli olarak aktarılacaktır.</p>
        <label>İşveren<input list="ap-emps" value={emp} onChange={(e) => setEmp(e.target.value)} placeholder="İşveren kısa adı" data-testid="quote-approve-employer" /><datalist id="ap-emps">{employers.map(e => <option key={e.id} value={e.short_name}>{e.name}</option>)}</datalist></label>
        <label>Sorumlu Şirket<select value={company} onChange={(e) => setCompany(e.target.value)} data-testid="quote-approve-company">{myCompanies.map(c => <option key={c}>{c}</option>)}</select></label>
        <button className="new-btn full btn-green" onClick={approve} disabled={busy} data-testid="quote-approve-confirm"><CheckCircle2 size={15} /> {busy ? "Oluşturuluyor…" : "Onayla ve Projeye Aktar"}</button>
      </div>
    </div>
  );
}
